/**
 * `UsageCard` — per-account usage visibility (ADR-0140 Phase B).
 *
 * Renders the four metering dimensions (`sites`, `transactions_monthly`,
 * `agent_traffic_monthly`, `red_team_runs_monthly`) as one row each, with
 * the consumed quantity vs. the EFFECTIVE limit (override → plan → null)
 * and a thin progress bar. The figures come from
 * `GET /internal/admin/accounts/:account_id/usage` — a fresh, uncached
 * count per request — fetched on mount.
 *
 * READ-ONLY and INERT. Limits aren't enforced yet (enforcement is
 * Phase C); the inline caption + sibling SubscriptionCard's banner make
 * that explicit. No writes, no mutate-on-click affordances.
 *
 * Visual contract:
 *   - "label · period-chip" on the left, "usage / limit" on the right.
 *   - One thin progress bar per row, color-banded:
 *       lime  / mint   when usage <= 80% of limit
 *       amber           when usage in (80%, 100%]
 *       coral           when usage > limit (over-quota — informational only)
 *     The bar is rendered muted-full-width when the dimension is
 *     unlimited (limit = null) so the row still has shape, but no value
 *     to fill toward.
 *   - When the row's effective entitlement carries a behavior
 *     (`hard` / `soft`), a small pill appears with a tooltip explaining
 *     what the behavior would do once enforcement ships.
 *   - When the route returned a `note` (e.g. "couldn't measure"), it
 *     renders below the row in a calm caption tone — no error chrome.
 *
 * States:
 *   - loading       → mono "Loading…" inside the card
 *   - error         → coral "Load failed" kicker + one-line reason
 *   - no plan       → every row renders with limit="Unlimited / no plan"
 *                     and the bar muted. (Same code path as a row whose
 *                     limit is genuinely NULL — distinguishing the two
 *                     would require an extra field; the user experience
 *                     is the same: "no enforced limit".)
 *
 * Responsive: each row is flex-col on mobile (label stacks above the
 * figure and bar) and flex-row on `sm`+. The progress bar is always
 * full-width inside its container, so it scales to the viewport without
 * bleed. Behavior pills wrap into their own row when space is tight.
 */

import { useEffect, useState } from 'react';

import type {
  AdminAccountUsageResponse,
  AdminAccountUsageRow,
  AdminUsageDimension,
  AdminUsagePeriod,
} from '@sill/types/api';
import type { ThemeTokens } from '@sill/ui';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { useC } from '../../lib/theme.js';
import { formatCount, formatIsoCompact } from '../../lib/format.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

/** Per-spec display labels (short — these sit next to a period chip
 *  that carries the time scope, so no "/ month" suffix needed). */
function usageDimensionLabel(d: AdminUsageDimension): string {
  switch (d) {
    case 'sites':
      return 'Sites';
    case 'transactions_monthly':
      return 'Transactions';
    case 'agent_traffic_monthly':
      return 'Agent traffic';
    case 'red_team_runs_monthly':
      return 'Red-team runs';
    // Not metered — never appears in USAGE_ROW_ORDER; the case exists
    // for switch exhaustiveness over the shared dimension enum.
    case 'retention_days':
      return 'Reporting history';
    // ADR-0263 §2.3 — v2 dimensions. UsageCard does NOT render them
    // in S1 (USAGE_ROW_ORDER keeps the v1 four); the labels exist
    // so the switch stays compile-exhaustive against the widened
    // AdminUsageDimension (= AdminPlanDimension).
    case 'page_loads_monthly':
      return 'Page loads';
    case 'orders_monthly':
      return 'Orders';
    case 'assistant_requests_monthly':
      return 'Assistant requests';
    case 'free_sites':
      return 'Free sites';
    case 'included_sites':
      return 'Included sites';
    case 'agency_scans_monthly':
      return 'Agency scans';
  }
}

/** Short period label for the kicker chip next to the dimension name. */
function periodChipLabel(p: AdminUsagePeriod): string {
  return p === 'lifetime' ? 'lifetime' : 'this month';
}

/** Display order — matches the plan-editor's `ALL_DIMENSIONS`. */
const USAGE_ROW_ORDER: readonly AdminUsageDimension[] = [
  'sites',
  'transactions_monthly',
  'agent_traffic_monthly',
  'red_team_runs_monthly',
];

export interface UsageCardProps {
  accountId: string;
}

export function UsageCard({ accountId }: UsageCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [data, setData] = useState<AdminAccountUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async (): Promise<void> => {
      try {
        const res = await api.getAccountUsage(accountId);
        if (!cancelled) setData(res);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? ` (${err.server_reason})`
            : '';
        setError(`Could not load usage${reason}.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, accountId]);

  // Re-sort the server payload into the canonical display order. The
  // server already emits stable-enum-order today, but doing this here
  // keeps the renderer robust to a future server-side change.
  const rowsByDim = new Map<AdminUsageDimension, AdminAccountUsageRow>();
  if (data !== null) {
    for (const row of data.usage) rowsByDim.set(row.dimension, row);
  }

  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Usage
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            letterSpacing: 1,
          }}
        >
          Usage vs. plan limits
        </div>
      </div>

      <div
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.5,
          marginBottom: 12,
        }}
      >
        Current consumption per metered dimension, surfaced fresh from the
        database on each load. Whether over-limit requests are blocked depends on
        this account&rsquo;s enforcement toggle (off by default).
      </div>

      {loading ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1.5,
            padding: '8px 0',
          }}
        >
          Loading…
        </div>
      ) : error !== null ? (
        <div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.coralFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Load failed
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
            }}
          >
            {error}
          </div>
        </div>
      ) : data !== null ? (
        <>
          <ul className="grid grid-cols-1 gap-2 list-none p-0 m-0">
            {USAGE_ROW_ORDER.map((dim) => {
              const row = rowsByDim.get(dim);
              if (row === undefined) {
                // The server contract is to always return all four
                // rows; if one is missing we still render a placeholder
                // so the column count stays predictable.
                return <MissingDimensionRow key={dim} dimension={dim} />;
              }
              return <UsageRowView key={dim} row={row} />;
            })}
          </ul>

          <div
            className="flex items-center justify-between flex-wrap gap-2"
            style={{ marginTop: 12 }}
          >
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.dim,
                letterSpacing: 1,
              }}
            >
              Computed {formatIsoCompact(data.computed_at)}
            </span>
          </div>
        </>
      ) : null}
    </Card>
  );
}

interface UsageRowViewProps {
  row: AdminAccountUsageRow;
}

function UsageRowView({ row }: UsageRowViewProps): JSX.Element {
  const C = useC();

  const label = usageDimensionLabel(row.dimension);
  const period = periodChipLabel(row.period);

  const unlimited = row.limit === null;
  const over = row.limit !== null && row.usage > row.limit;
  const figure = unlimited
    ? `${formatCount(row.usage)} / Unlimited`
    : `${formatCount(row.usage)} / ${formatCount(row.limit)}`;

  const ratio = computeRatio(row.usage, row.limit);
  const tone = barTone(ratio, unlimited, over);
  const barColors = barPalette(C, tone);

  return (
    <li
      className="flex flex-col gap-2 px-3 py-3"
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
      }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 500,
              color: C.text,
            }}
          >
            {label}
          </span>
          <PeriodChip label={period} />
          {row.behavior !== null ? (
            <BehaviorChip behavior={row.behavior} />
          ) : null}
        </div>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: over ? C.coralFg : C.body,
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
          aria-label={`Usage ${formatCount(row.usage)} of ${
            unlimited ? 'unlimited' : formatCount(row.limit)
          }`}
        >
          {figure}
        </span>
      </div>

      <UsageBar
        ratio={ratio}
        unlimited={unlimited}
        barColors={barColors}
        ariaLabel={`${label} usage progress`}
      />

      {row.note !== undefined && row.note.length > 0 ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 11.5,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          {row.note}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Fallback row when the server omits a dimension. Shouldn't happen
 * under the documented contract; renders a neutral, low-noise placeholder
 * rather than crashing or hiding the row entirely.
 */
function MissingDimensionRow({
  dimension,
}: {
  dimension: AdminUsageDimension;
}): JSX.Element {
  const C = useC();
  return (
    <li
      className="flex flex-col gap-1 px-3 py-3"
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
      }}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            fontWeight: 500,
            color: C.text,
          }}
        >
          {usageDimensionLabel(dimension)}
        </span>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1,
          }}
        >
          —
        </span>
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11.5,
          color: C.muted,
        }}
      >
        Server did not return a row for this dimension.
      </div>
    </li>
  );
}

/** Calm grey mono chip — period scope ("this month" / "lifetime"). */
function PeriodChip({ label }: { label: string }): JSX.Element {
  const C = useC();
  return (
    <span
      className="inline-flex items-center px-2 py-0.5"
      style={{
        background: C.surface,
        color: C.muted,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

/** Behavior chip — `hard` / `soft` with an explainer tooltip. The
 *  tooltip is rendered as a `title` attribute so it is keyboard- /
 *  screen-reader accessible without an extra runtime popover library. */
function BehaviorChip({
  behavior,
}: {
  behavior: 'hard' | 'soft';
}): JSX.Element {
  const C = useC();
  const isHard = behavior === 'hard';
  const fg = isHard ? C.coralFg : C.amberFg;
  const bg = isHard ? C.coralDim : C.amberDim;
  const bd = isHard
    ? 'rgba(255,107,71,0.3)'
    : 'rgba(255,182,39,0.3)';
  return (
    <span
      className="inline-flex items-center px-2 py-0.5"
      title={
        isHard
          ? 'hard = will block once enforcement is on; soft = allowed, overage tracked.'
          : 'soft = allowed, overage tracked; hard = will block once enforcement is on.'
      }
      style={{
        background: bg,
        color: fg,
        border: `1px solid ${bd}`,
        borderRadius: 2,
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {behavior}
    </span>
  );
}

interface UsageBarProps {
  /** Clamped ratio in [0, 1]; meaningless when `unlimited`. */
  ratio: number;
  unlimited: boolean;
  barColors: { track: string; fill: string };
  ariaLabel: string;
}

function UsageBar({
  ratio,
  unlimited,
  barColors,
  ariaLabel,
}: UsageBarProps): JSX.Element {
  // Width is 100% of the track when unlimited (informational fill);
  // the fill color is muted so the row doesn't read as "at capacity".
  const widthPct = unlimited ? 100 : Math.max(0, Math.min(1, ratio)) * 100;
  return (
    <div
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={unlimited ? undefined : Math.round(widthPct)}
      style={{
        background: barColors.track,
        height: 4,
        borderRadius: 2,
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <div
        style={{
          background: barColors.fill,
          height: '100%',
          width: `${widthPct}%`,
          transition: 'width 240ms ease',
        }}
      />
    </div>
  );
}

type BarTone = 'normal' | 'warn' | 'over' | 'muted';

function computeRatio(usage: number, limit: number | null): number {
  if (limit === null || limit <= 0) return 0;
  return usage / limit;
}

function barTone(ratio: number, unlimited: boolean, over: boolean): BarTone {
  if (unlimited) return 'muted';
  if (over) return 'over';
  if (ratio >= 0.8) return 'warn';
  return 'normal';
}

function barPalette(
  C: ThemeTokens,
  tone: BarTone,
): { track: string; fill: string } {
  switch (tone) {
    case 'normal':
      return { track: C.surface3, fill: C.limeFg };
    case 'warn':
      return { track: C.surface3, fill: C.amberFg };
    case 'over':
      return { track: C.coralDim, fill: C.coralFg };
    case 'muted':
      return { track: C.surface3, fill: C.dim };
  }
}
